/**
 * Whether a partner offering can actually be visited ("Ik ga naar de aanbieder").
 *
 * Two things must hold, and the reported bugs came from checking neither:
 * 1. The activity is configured for it (`hasOwnLocations` / `hasFixedLocations` — the same
 *    pair the search "FIXED" filter uses). Legacy rows with both flags `null` fall back to
 *    the address data.
 * 2. There is a real venue to show. `PartnerOption.addresses` also carries the synthetic
 *    partner-HQ coordinate (`isHeadquarters`) that `getGroupPartners` pushes when an activity
 *    has no `partner_address` rows; that point exists for distance/centering only and is not
 *    a bookable location, so it must never make the fixed tab look available.
 */
/** The synthetic partner-HQ point, whatever address shape the caller holds. */
function isHeadquarters(address: unknown): boolean {
    return (
        typeof address === 'object' &&
        address !== null &&
        'isHeadquarters' in address &&
        address.isHeadquarters === true
    );
}

export function partnerHasFixedVenue(partner: {
    addresses?: ReadonlyArray<unknown> | null;
    hasOwnLocations?: boolean | null;
    hasFixedLocations?: boolean | null;
}): boolean {
    const hasRealVenue = !!partner.addresses?.some(address => !isHeadquarters(address));
    if (partner.hasOwnLocations == null && partner.hasFixedLocations == null) return hasRealVenue;
    return hasRealVenue && (partner.hasOwnLocations === true || partner.hasFixedLocations === true);
}

/**
 * Whether the activity happens at a set location whose address the partner never entered.
 *
 * The partner form's middle option ("a fixed location that is not yours" — a city centre, a
 * meadow) sets `hasFixedLocations` without necessarily producing a `partner_address` row. Such
 * an activity has nowhere to pin on the map, but it very much has somewhere to happen, and
 * treating it as "no venue" left the widget striking through both tabs: the page then said the
 * activity could take place nowhere at all, which is the one thing that is certainly untrue.
 */
export function partnerVenueIsUnpinned(partner: {
    addresses?: ReadonlyArray<unknown> | null;
    hasOwnLocations?: boolean | null;
    hasFixedLocations?: boolean | null;
}): boolean {
    // `undefined` means the addresses were never loaded, not that there are none — reading it
    // as "unpinned" would send an activity with real venues to the travelling tab.
    if (!Array.isArray(partner.addresses)) return false;
    return partner.hasFixedLocations === true && !partnerHasFixedVenue(partner);
}
